import React from 'react';

export const slugify = (s) => String(s).replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase();

export const H3 = ({ id, children }) => (
  <h3 id={id} className="scroll-mt-28 text-xl lg:text-2xl font-bold tracking-tight mt-16 mb-4 text-white" data-testid={`tech-h3-${id}`}>{children}</h3>
);

export const Lead = ({ children }) => <p className="text-base lg:text-lg text-white/70 leading-relaxed max-w-3xl mb-6">{children}</p>;

export const Purpose = ({ children }) => (
  <p className="text-sm lg:text-base text-white/55 leading-relaxed max-w-3xl mb-6 border-l-2 border-[#FF7A00]/60 pl-4">{children}</p>
);

export const P = ({ children }) => <p className="text-sm lg:text-base text-white/60 leading-relaxed max-w-3xl mb-6">{children}</p>;

export const TCap = ({ title, children }) => (
  <p className="text-sm text-white/50 mb-3 mt-6"><b className="text-white/85 font-semibold">{title}</b>{children ? <> {children}</> : null}</p>
);

export const FigCap = ({ n, children }) => (
  <figcaption className="text-xs lg:text-sm text-white/45 leading-relaxed mt-3 max-w-4xl">
    {children}
  </figcaption>
);

export const Fig = ({ n, cap, children, className = '' }) => (
  <figure className={`my-8 ${className}`}>{children}{cap && <FigCap n={n}>{cap}</FigCap>}</figure>
);

export const Num = ({ children }) => <span className="font-mono text-[#FFB066]">{children}</span>;

const Shell = ({ children, min }) => (
  <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#111111] mb-4">
    <table className="w-full text-sm border-collapse" style={{ minWidth: min }}>{children}</table>
  </div>
);

const Th = ({ children, right }) => (
  <th className={`px-4 py-3 font-mono text-[11px] tracking-[0.14em] uppercase text-white/40 font-semibold border-b border-white/10 ${right ? 'text-right' : 'text-left'}`}>{children}</th>
);

export const SpecTable = ({ rows, head, testid, min = 560 }) => (
  <Shell min={min}>
    {head && <thead><tr>{head.map((h) => <Th key={h}>{h}</Th>)}</tr></thead>}
    <tbody data-testid={testid}>
      {rows.map(([k, v]) => (
        <tr key={k} className="border-t border-white/[0.06] first:border-t-0 hover:bg-white/[0.02] transition-colors">
          <td className="px-4 py-3 align-top text-white/90 font-medium w-[34%]">{k}</td>
          <td className="px-4 py-3 align-top text-white/60 leading-relaxed">{v}</td>
        </tr>
      ))}
    </tbody>
  </Shell>
);

export const DataTable = ({ head, rows, right = [], testid, min = 640 }) => (
  <Shell min={min}>
    <thead><tr>{head.map((h, i) => <Th key={h} right={right.includes(i)}>{h}</Th>)}</tr></thead>
    <tbody data-testid={testid}>
      {rows.map((r, j) => (
        <tr key={j} className="border-t border-white/[0.06] first:border-t-0 hover:bg-white/[0.02] transition-colors">
          {r.map((c, i) => (
            <td key={i} className={`px-4 py-3 align-top leading-relaxed ${i === 0 ? 'text-white/90 font-medium' : 'text-white/60'} ${right.includes(i) ? 'text-right font-mono whitespace-nowrap' : ''}`}>{c}</td>
          ))}
        </tr>
      ))}
    </tbody>
  </Shell>
);

export const Code = ({ children, testid }) => (
  <pre className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-5 overflow-x-auto text-[12px] leading-relaxed font-mono text-white/70 whitespace-pre" data-testid={testid}>{children}</pre>
);

export const Video = ({ name, caption, testid }) => (
  <figure className="m-0">
    <video controls muted playsInline loop preload="metadata" poster={`/tech/media/${name}.jpg`} src={`/tech/media/${name}.mp4`}
      className="w-full rounded-xl border border-white/10 bg-black block" data-testid={testid || `video-${slugify(name)}`} />
    {caption && <figcaption className="text-xs text-white/45 mt-2">{caption}</figcaption>}
  </figure>
);

export const Chips = ({ items, ran = [] }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map((m) => (
      <span key={m} className={`rounded-full border px-2.5 py-1 text-xs ${ran.includes(m) ? 'border-[#FF7A00] text-[#FF7A00] bg-[#FF7A00]/10 font-semibold' : 'border-white/10 text-white/60 bg-white/[0.03]'}`}>{m}</span>
    ))}
  </div>
);
